import memoize, { type MemoizedFn } from 'memoize-one';

import { LS_PROVIDER_KEY, LS_PROVIDER_MAPPING_KEY, PROVIDERS_API } from './constants';
import type { ProviderMappingData, ProviderRegexResponse } from './types';

export type RegexStorage = {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
};

/**
 * Check if the url is supported by Linking Platform by
 * testing it against any of the regex patterns
 * @param url string
 * @returns boolean
 */
export const isSupportedByLinkingPlatform = (url: string, regexStorage: RegexStorage): boolean => {
	const cachedProviderRegex = getProviderRegexFromLs(regexStorage);

	if (!cachedProviderRegex.length || !url.length) {
		return false;
	}

	try {
		const regexPattern = new RegExp(cachedProviderRegex, 'g');
		return regexPattern.test(url);
	} catch {
		// Malformed joined provider regex (e.g. duplicate named capture groups) — treat as unsupported.
		return false;
	}
};

/**
 * Get the currently stored provider regex patterns from LS
 * @returns Json<ProviderRegexStorage>
 */
export const getProviderRegexFromLs = (regexStorage: RegexStorage): string => {
	if (regexStorage.getItem(LS_PROVIDER_KEY)) {
		return JSON.parse(`${regexStorage.getItem(LS_PROVIDER_KEY)}`);
	}
	return '';
};

/**
 * setProviderRegexLs - set the provider regex patterns in Storage for
 * easy access within the synchronous md tokenizers
 * @param providerRegex string - combined regex patterns from Linking Platform to identify supported urls
 */
export const setProviderRegexLs = (regexStorage: RegexStorage, providerRegex: string): void => {
	regexStorage?.setItem(LS_PROVIDER_KEY, JSON.stringify(providerRegex));
};

/**
 * hydrateLinkingPlatformCache - fetch the provider regex patterns from Linking Platform
 * This also stores the provider mapping with keys for later retrieval
 */
export const hydrateLinkingPlatformCache = async (
	regexStorage: RegexStorage,
	baseUrl?: string,
): Promise<void> => {
	const providerData = await getProviderDataFromLinkingPlatform(baseUrl);
	if (providerData) {
		const providerRegex = reduceProviderRegex(providerData);
		setProviderRegexLs(regexStorage, providerRegex);

		const providerMapping = buildProviderMapping(providerData);
		setProviderMappingLs(regexStorage, providerMapping);
	}
};

/**
 * reduceProviderRegex
 * @param data - ProviderRegexResponse
 * @returns string - combined regex patterns from Linking Platform for a single regex test
 * to avoid individual tests on a loop of the providers
 */
export const reduceProviderRegex = (data: ProviderRegexResponse): string => {
	if (!data?.providers || !Array.isArray(data.providers)) {
		return '';
	}

	const sources = data.providers.reduce((acc, provider) => {
		const providerSources = provider.patterns?.map((pattern) => pattern.source) || [];
		return [...acc, ...providerSources];
	}, [] as string[]);

	return sources.join('|');
};

/**
 * buildProviderMapping
 * @param data - ProviderRegexResponse
 * @returns ProviderMappingData - combined regex and individual patterns with provider keys
 */
export const buildProviderMapping = (data: ProviderRegexResponse): ProviderMappingData => {
	if (!data?.providers || !Array.isArray(data.providers)) {
		return {
			combinedRegex: '',
			patterns: [],
		};
	}

	const patterns = data.providers.flatMap((provider) =>
		(provider.patterns || []).map((pattern) => ({
			key: provider.key,
			regex: pattern.source,
		})),
	);

	const combinedRegex = patterns.map((p) => p.regex).join('|');

	return {
		combinedRegex,
		patterns,
	};
};

/**
 * Get the provider mapping from storage
 * @returns ProviderMappingData | null
 */
export const getProviderMappingFromLs = (
	regexStorage: RegexStorage,
): ProviderMappingData | null => {
	const stored = regexStorage.getItem(LS_PROVIDER_MAPPING_KEY);
	if (stored) {
		try {
			return JSON.parse(stored);
		} catch {
			return null;
		}
	}
	return null;
};

/**
 * Set the provider mapping in storage
 * @param providerMapping - ProviderMappingData
 */
export const setProviderMappingLs = (
	regexStorage: RegexStorage,
	providerMapping: ProviderMappingData,
): void => {
	regexStorage?.setItem(LS_PROVIDER_MAPPING_KEY, JSON.stringify(providerMapping));
};

/**
 * Get provider data from Linking Platform
 * This is called to hydrate localStorage with every `ContentRenderer` mount, so we're
 * memoizing the response to avoid unnecessary network requests
 * @returns ProviderRegexResponse | null
 */
export const getProviderDataFromLinkingPlatform: MemoizedFn<
	(baseUrl?: string) => Promise<ProviderRegexResponse | null>
> = memoize(async (baseUrl: string = ''): Promise<ProviderRegexResponse | null> => {
	try {
		const response = await fetch(baseUrl + PROVIDERS_API, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				type: 'resolve',
			}),
		});
		const data: ProviderRegexResponse = await response.json();
		return data || null;
	} catch {
		return null;
	}
});

/**
 * Get a list of regex patterns for all supported providers from Linking Platform
 * @deprecated Use getProviderDataFromLinkingPlatform instead for better functionality
 * @returns string - combined regex patterns
 */
export const getProviderRegexFromLinkingPlatform: MemoizedFn<
	(baseUrl?: string) => Promise<string>
> = memoize(async (baseUrl: string = ''): Promise<string> => {
	const data = await getProviderDataFromLinkingPlatform(baseUrl);
	return data ? reduceProviderRegex(data) : '';
});
